import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { getApiUrl } from './utils/config';
import { toast } from 'sonner';

const API = getApiUrl();

const AdminDirectTest = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [testResults, setTestResults] = useState([]);

  const addResult = (test, success, message) => {
    setTestResults(prev => [...prev, { test, success, message, time: new Date().toLocaleTimeString() }]);
  };

  useEffect(() => {
    const loadUsers = async () => {
      try {
        const token = localStorage.getItem('token');
        if (!token) {
          addResult('Token Check', false, 'No token found in localStorage');
          setLoading(false);
          return;
        }

        addResult('Token Check', true, `Token found: ${token.substring(0, 20)}...`);

        const headers = { 'Authorization': `Bearer ${token}` };
        const response = await axios.get(`${API}/admin/users`, { headers });
        
        setUsers(response.data || []);
        addResult('User List', true, `Loaded ${response.data?.length || 0} users`);
      } catch (error) {
        console.error('Load users error:', error);
        addResult('User List', false, error.response?.data?.detail || error.message);
      }
      setLoading(false);
    };

    loadUsers();
  }, []);

  const testPasswordReset = async () => {
    if (users.length === 0) {
      addResult('Password Reset', false, 'No users available for testing');
      return;
    }

    // Find a test user (not admin)
    const testUser = users.find(u => u.email !== 'admin@carcare.com' && u.role === 'user');
    if (!testUser) {
      addResult('Password Reset', false, 'No suitable test user found');
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const headers = { 'Authorization': `Bearer ${token}` };
      
      const response = await axios.post(`${API}/admin/users/${testUser.id}/reset-password`, {
        new_password: 'TestPassword123!'
      }, { headers });

      addResult('Password Reset', true, `Reset password for ${testUser.email}: ${response.data.message}`);
      toast.success('Password reset test successful!');
    } catch (error) {
      console.error('Password reset error:', error);
      addResult('Password Reset', false, error.response?.data?.detail || error.message);
      toast.error('Password reset test failed');
    }
  };

  const testUserDeletion = async () => {
    // We won't actually delete a user, just test the API call
    if (users.length === 0) {
      addResult('User Deletion', false, 'No users available for testing');
      return;
    }

    // Find a test user
    const testUser = users.find(u => u.email !== 'admin@carcare.com' && u.email.includes('test'));
    if (!testUser) {
      addResult('User Deletion', false, 'No suitable test user found (looking for test users only)');
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const headers = { 'Authorization': `Bearer ${token}` };
      
      // We'll just simulate this - not actually delete
      addResult('User Deletion (Simulated)', true, `Would delete user: ${testUser.email} (ID: ${testUser.id})`);
      toast.success('User deletion test ready (not executed)');
    } catch (error) {
      addResult('User Deletion', false, error.message);
    }
  };

  const testUserProfile = async () => {
    try {
      const token = localStorage.getItem('token');
      const headers = { 'Authorization': `Bearer ${token}` };
      
      const response = await axios.get(`${API}/auth/me`, { headers });
      const user = response.data;
      
      addResult('User Profile', true, `User: ${user.email}, Role: ${user.role}, Name: ${user.name}`);
    } catch (error) {
      addResult('User Profile', false, error.response?.data?.detail || error.message);
    }
  };

  if (loading) {
    return <div className="p-6">Loading admin test...</div>;
  }

  return (
    <div className="p-6 max-w-4xl mx-auto">
      <h1 className="text-2xl font-bold mb-6">Admin Functions Direct Test</h1>
      
      <div className="mb-6 space-y-4">
        <button
          onClick={testUserProfile}
          className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700 mr-4"
        >
          Test User Profile
        </button>
        <button
          onClick={testPasswordReset}
          className="bg-green-600 text-white px-4 py-2 rounded hover:bg-green-700 mr-4"
        >
          Test Password Reset
        </button>
        <button
          onClick={testUserDeletion}
          className="bg-red-600 text-white px-4 py-2 rounded hover:bg-red-700 mr-4"
        >
          Test User Deletion (Simulate)
        </button>
      </div>

      <div className="bg-gray-100 p-4 rounded mb-6">
        <h3 className="font-bold mb-2">Test Results:</h3>
        <div className="space-y-2 max-h-60 overflow-y-auto">
          {testResults.map((result, index) => (
            <div key={index} className={`text-sm p-2 rounded ${result.success ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
              <strong>{result.time} - {result.test}:</strong> {result.message}
            </div>
          ))}
        </div>
      </div>

      <div className="bg-white border rounded p-4">
        <h3 className="font-bold mb-2">Available Users ({users.length}):</h3>
        <div className="max-h-40 overflow-y-auto">
          {users.slice(0, 10).map(user => (
            <div key={user.id} className="text-sm py-1 border-b">
              {user.email} - {user.role} ({user.name})
            </div>
          ))}
          {users.length > 10 && <div className="text-sm text-gray-500">... and {users.length - 10} more</div>}
        </div>
      </div>
    </div>
  );
};

export default AdminDirectTest;